import { CommonModule } from '@angular/common';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import {
  AppButtonConfig,
  ButtonHtmlType,
  ButtonSize,
  ButtonVariant,
} from './button.types';

/**
 * Enterprise-grade reusable button for the application.
 *
 * Drop-in replacement for native `<button class="btn btn-*">` when `compatBootstrap` is true (default).
 *
 * @example
 * <app-button label="Save" variant="primary" (buttonClick)="save()"></app-button>
 */
@Component({
  selector: 'app-button',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ButtonComponent implements AfterContentInit, AppButtonConfig {
  @Input() label = '';
  @Input() type: ButtonHtmlType = 'button';
  @Input() variant: ButtonVariant | string = ButtonVariant.Primary;
  @Input() size: ButtonSize | string = ButtonSize.Md;
  @Input() disabled = false;
  @Input() loading = false;
  @Input() iconLeft = '';
  @Input() iconRight = '';
  @Input() fullWidth = false;
  @Input() customClass = '';

  /** Toggled/selected visual state (e.g. filters panel open). */
  @Input() active = false;

  /** When set, navigates via Angular router (replaces button + routerLink pattern). */
  @Input() routerLink: string | unknown[] | null = null;

  /** Accessible name when the visible label is empty (e.g. icon-only buttons). */
  @Input() ariaLabel = '';

  /** Native title tooltip. */
  @Input() title = '';

  /**
   * When true, applies Bootstrap `btn` / `btn-*` classes for minimal migration from legacy markup.
   * Default false so Meal Dynamics Figma tokens take precedence.
   */
  @Input() compatBootstrap = false;

  @Output() readonly buttonClick = new EventEmitter<MouseEvent>();

  @ViewChild('contentWrapper', { static: true })
  private readonly contentWrapper!: ElementRef<HTMLElement>;

  /** Whether projected content replaced the text label. */
  hasProjectedContent = false;

  readonly ButtonVariant = ButtonVariant;
  readonly ButtonSize = ButtonSize;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngAfterContentInit(): void {
    this.hasProjectedContent = this.detectProjectedContent();
    this.cdr.markForCheck();
  }

  get isInteractionBlocked(): boolean {
    return this.disabled || this.loading;
  }

  get computedAriaLabel(): string | null {
    if (this.ariaLabel?.trim()) {
      return this.ariaLabel.trim();
    }
    if (this.label?.trim()) {
      return this.label.trim();
    }
    return null;
  }

  get buttonClasses(): string[] {
    const classes: string[] = ['app-btn'];

    if (this.compatBootstrap) {
      classes.push('btn');
    }

    classes.push(`app-btn--${this.variant}`);

    if (this.compatBootstrap) {
      classes.push(this.bootstrapVariantClass);
    }

    if (this.size !== ButtonSize.Md) {
      classes.push(`app-btn--${this.size}`);
      if (this.compatBootstrap) {
        classes.push(`btn-${this.size}`);
      }
    }

    if (this.fullWidth) {
      classes.push('app-btn--full-width');
    }

    if (this.loading) {
      classes.push('app-btn--loading');
    }

    if (this.isIconOnly) {
      classes.push('app-btn--icon-only');
      if (this.compatBootstrap) {
        classes.push('btn-icon');
      }
    }

    if (this.customClass?.trim()) {
      classes.push(...this.customClass.trim().split(/\s+/));
    }

    if (this.active) {
      classes.push('active');
    }

    return classes;
  }

  get bootstrapVariantClass(): string {
    if (this.variant === ButtonVariant.Outline) {
      return 'btn-outline-primary';
    }
    return `btn-${this.variant}`;
  }

  get isIconOnly(): boolean {
    const hasIcon = Boolean(this.iconLeft?.trim() || this.iconRight?.trim());
    const hasLabel = Boolean(this.label?.trim()) || this.hasProjectedContent;
    return hasIcon && !hasLabel;
  }

  get showLabel(): boolean {
    return Boolean(this.label?.trim()) && !this.hasProjectedContent;
  }

  get showLeftIcon(): boolean {
    return Boolean(this.iconLeft?.trim()) && !this.loading;
  }

  get showRightIcon(): boolean {
    return Boolean(this.iconRight?.trim()) && !this.loading;
  }

  onButtonClick(event: MouseEvent): void {
    if (this.isInteractionBlocked) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.buttonClick.emit(event);
  }

  onKeydown(event: KeyboardEvent): void {
    if (this.isInteractionBlocked && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  private detectProjectedContent(): boolean {
    const el = this.contentWrapper?.nativeElement;
    if (!el) {
      return false;
    }
    return Array.from(el.childNodes).some((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        return true;
      }
      if (node.nodeType === Node.TEXT_NODE) {
        return Boolean(node.textContent?.trim());
      }
      return false;
    });
  }
}
